/**
 * Body detection check — Prompt.md §10 ("chỉ cho phép Calibration khi phát hiện đủ landmark"),
 * §16 (confidence filtering).
 */

import type { AppConfig } from "@studystraight/shared-config";
import type {
  CameraPlacement,
  LandmarkMap,
  LandmarkName,
  VisionResult,
} from "@studystraight/shared-types";

import { distance } from "./geometry.js";

/**
 * Landmark **bắt buộc** để bắt đầu calibration, khác nhau theo placement (quyết định #19):
 * - FRONTAL: hông là optional. Bàn học che hông là chuyện thường xuyên (DU #3); nếu bắt buộc,
 *   phần lớn người dùng sẽ không qua được bước setup — trong khi mode này vẫn đo được
 *   head down / too close / shoulder tilt mà không cần hông.
 * - SIDE: hông **bắt buộc**, vì giá trị duy nhất mode này mang lại là đo `torsoAngle` trực tiếp
 *   từ vai↔hông. Không thấy hông thì mode SIDE không hơn gì FRONTAL.
 */
export const REQUIRED_LANDMARKS: Record<CameraPlacement, readonly LandmarkName[]> = {
  FRONTAL: ["nose", "leftShoulder", "rightShoulder"],
  SIDE: ["nose", "leftShoulder", "rightShoulder", "leftHip", "rightHip"],
};

export type DetectionIssue = "MISSING_LANDMARKS" | "LOW_CONFIDENCE";

export interface DetectionQuality {
  ok: boolean;
  missing: LandmarkName[];
  /**
   * Nghi ngờ người dùng chọn sai mode (quyết định #21).
   * Đây chỉ là tín hiệu **một frame**; UI phải tự giữ nó liên tục
   * `VisionConfig.placementMismatch.sustainedMs` trước khi hiện banner, nếu không banner sẽ
   * nhấp nháy mỗi lần trẻ quay đầu.
   */
  placementMismatch?: { suspected: CameraPlacement; ratio: number };
  reason?: DetectionIssue;
}

/** Landmark có được engine **thực sự thấy**, chứ không phải suy đoán vị trí. */
export function isLandmarkVisible(
  landmarks: LandmarkMap,
  name: LandmarkName,
  minVisibility: number,
): boolean {
  const lm = landmarks[name];
  if (lm === undefined) return false;
  // visibility undefined = engine không cung cấp chỉ số này → tin vào việc nó có trả landmark.
  return (lm.visibility ?? 1) >= minVisibility;
}

/**
 * Confidence tổng hợp cho `VisionResult.confidence`: visibility **nhỏ nhất** trong các landmark
 * bắt buộc, không phải trung bình. Trung bình cho phép một landmark bị che hoàn toàn ẩn mình
 * sau bốn landmark rõ nét — mà chỉ cần thiếu một điểm bắt buộc là công thức đã sai.
 */
export function computeConfidence(landmarks: LandmarkMap, placement: CameraPlacement): number {
  let min = 1;
  for (const name of REQUIRED_LANDMARKS[placement]) {
    const lm = landmarks[name];
    if (lm === undefined) return 0;
    min = Math.min(min, lm.visibility ?? 1);
  }
  return min;
}

/**
 * `shoulderWidth / earDistance`: ở chính diện vai rộng hơn khoảng cách hai tai nhiều lần;
 * ở profile view hai vai gần như chồng nhau nên tỉ lệ tụt mạnh. Trả `undefined` khi thiếu điểm.
 */
export function shoulderEarRatio(result: VisionResult, minVisibility: number): number | undefined {
  const { landmarks, frameAspectRatio } = result;
  const names = ["leftEar", "rightEar", "leftShoulder", "rightShoulder"] as const;
  if (names.some((n) => !isLandmarkVisible(landmarks, n, minVisibility))) return undefined;

  const { leftEar, rightEar, leftShoulder, rightShoulder } = landmarks;
  if (!leftEar || !rightEar || !leftShoulder || !rightShoulder) return undefined;

  const earDistance = distance(leftEar, rightEar, frameAspectRatio);
  if (earDistance < 1e-6) return undefined;
  return distance(leftShoulder, rightShoulder, frameAspectRatio) / earDistance;
}

function detectPlacementMismatch(
  result: VisionResult,
  config: AppConfig,
): DetectionQuality["placementMismatch"] {
  const ratio = shoulderEarRatio(result, config.posture.minLandmarkVisibility);
  if (ratio === undefined) return undefined;

  const { frontalMinShoulderEarRatio, sideMaxShoulderEarRatio } = config.vision.placementMismatch;
  if (config.placement === "FRONTAL" && ratio < frontalMinShoulderEarRatio) {
    return { suspected: "SIDE", ratio };
  }
  if (config.placement === "SIDE" && ratio > sideMaxShoulderEarRatio) {
    return { suspected: "FRONTAL", ratio };
  }
  return undefined;
}

/**
 * Dùng ở Camera Setup (§10) để gate nút "Bắt đầu hiệu chỉnh", và trong monitoring loop để phân
 * biệt **mất tracking** với **lỗi tư thế** (§15) — hai thứ này hiển thị khác nhau và tuyệt đối
 * không được phát alert lẫn nhau.
 *
 * Nhận `AppConfig` (đã resolve theo placement) thay vì `(placement, config)` như bản nháp của
 * plan: `AppConfig.placement` đã mang thông tin đó, truyền cả hai mở đường cho lỗi hai nguồn
 * sự thật lệch nhau.
 */
export function assessDetectionQuality(result: VisionResult, config: AppConfig): DetectionQuality {
  const { minLandmarkVisibility, minFrameConfidence } = config.posture;

  const missing = REQUIRED_LANDMARKS[config.placement].filter(
    (name) => !isLandmarkVisible(result.landmarks, name, minLandmarkVisibility),
  );

  const reason: DetectionIssue | undefined =
    missing.length > 0
      ? "MISSING_LANDMARKS"
      : result.confidence < minFrameConfidence
        ? "LOW_CONFIDENCE"
        : undefined;

  const quality: DetectionQuality = { ok: reason === undefined, missing };
  if (reason !== undefined) quality.reason = reason;

  const placementMismatch = detectPlacementMismatch(result, config);
  if (placementMismatch !== undefined) quality.placementMismatch = placementMismatch;

  return quality;
}
